import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList,
  Search,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Warning,
  Block,
  Assignment,
  Schedule,
  Person,
  Comment,
  AttachFile,
  PlayArrow,
  Pause,
  Done,
  RadioButtonUnchecked
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { odosActivityService, ODOSActivity, odosModuleService, ODOSModule } from '../../services/odosService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ODOSActivities: React.FC = () => {
  const navigate = useNavigate();
  const { id: moduleId } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activities, setActivities] = useState<ODOSActivity[]>([]);
  const [modules, setModules] = useState<ODOSModule[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ODOSActivity[]>([]);
  const [stats, setStats] = useState<any>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter] = useState<string>(moduleId || 'all');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ODOSActivity | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [activities, searchTerm, statusFilter, priorityFilter, moduleFilter, tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [activitiesRes, modulesRes, statsRes] = await Promise.all([
        odosActivityService.getActivities(),
        odosModuleService.getModules(),
        odosActivityService.getActivityStats()
      ]);
      
      setActivities(activitiesRes.data);
      setModules(modulesRes.data);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activities');
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...activities];

    // Tab-based filtering
    switch (tabValue) {
      case 1: // My Activities
        filtered = filtered.filter(a => a.assignedTo); // Would check against current user
        break;
      case 2: // In Progress
        filtered = filtered.filter(a => a.status === 'in-progress');
        break;
      case 3: // Blocked
        filtered = filtered.filter(a => a.status === 'blocked');
        break;
      case 4: // Overdue
        filtered = filtered.filter(a => {
          if (!a.dueDate) return false;
          return new Date(a.dueDate) < new Date() && a.status !== 'completed';
        });
        break;
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(a => a.priority === priorityFilter);
    }

    // Module filter
    if (moduleFilter !== 'all') {
      filtered = filtered.filter(a => {
        // Handle both populated objects and string IDs
        const activityModuleId = typeof a.module === 'object' ? a.module._id : a.module;
        return activityModuleId === moduleFilter;
      });
    }

    setFilteredActivities(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewActivity = (activity: ODOSActivity) => {
    setSelectedActivity(activity);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivity(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'info';
      case 'blocked': return 'error';
      case 'review': return 'warning';
      case 'planned': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const getModuleName = (moduleId: string | ODOSModule) => {
    if (typeof moduleId === 'object') {
      return moduleId.name;
    }
    const module = modules.find(m => m._id === moduleId);
    return module?.name || 'Unknown Module';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading activities...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontFamily: 'Merriweather, serif' }}>
            {moduleId ? `Module Activities` : 'ODOS Activities'}
          </Typography>
          {moduleId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/odos/activities')}
                sx={{ color: 'primary.main', textTransform: 'none' }}
              >
                ← Back to All Activities
              </Button>
              <Typography variant="subtitle1" color="text.secondary">
                Activities for {getModuleName(moduleId)}
              </Typography>
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/odos/activities/new')}
          sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          New Activity
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h4">{stats.total || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.byStatus?.find((s: any) => s._id === 'in-progress')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Blocked
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.byStatus?.find((s: any) => s._id === 'blocked')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.byStatus?.find((s: any) => s._id === 'completed')?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="backlog">Backlog</MenuItem>
                  <MenuItem value="planned">Planned</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  value={moduleFilter}
                  label="Module"
                  onChange={(e) => setModuleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Modules</MenuItem>
                  {modules.map((module) => (
                    <MenuItem key={module._id} value={module._id}>
                      {module.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPriorityFilter('all');
                  setModuleFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Activity tabs">
          <Tab
            label={moduleId ? "All Module Activities" : "All Activities"}
            icon={<Badge badgeContent={filteredActivities.length} color="primary" />}
            iconPosition="end"
          />
          <Tab
            label="My Activities"
            icon={<Person />}
            iconPosition="start"
          />
          <Tab
            label="In Progress"
            icon={<Badge badgeContent={activities.filter(a => a.status === 'in-progress').length} color="info" />}
            iconPosition="end"
          />
          <Tab
            label="Blocked"
            icon={<Badge badgeContent={activities.filter(a => a.status === 'blocked').length} color="error" />}
            iconPosition="end"
          />
          <Tab
            label="Overdue"
            icon={<Warning />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Activities Table */}
      <TabPanel value={tabValue} index={tabValue}>
        {filteredActivities.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Activity ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredActivities.map((activity) => (
                  <TableRow
                    key={activity._id}
                    hover
                    sx={{ '&:hover': { cursor: 'pointer', bgcolor: '#f9f9f9' } }}
                    onClick={() => handleViewActivity(activity)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {activity.activityId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {activity.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {getModuleName(activity.module)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={activity.status}
                        size="small"
                        color={getStatusColor(activity.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={activity.priority}
                        size="small"
                        sx={{
                          bgcolor: getPriorityColor(activity.priority),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={activity.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {activity.assignedTo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {activity.assignedTo.toString().charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="caption">Assigned</Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.dueDate ? (
                        <Typography
                          variant="caption"
                          color={new Date(activity.dueDate) < new Date() ? 'error' : 'text.secondary'}
                        >
                          {new Date(activity.dueDate).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No due date
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.actualHours !== undefined && activity.estimatedHours ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((activity.actualHours / activity.estimatedHours) * 100, 100)}
                            sx={{ width: 60, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption">
                            {activity.actualHours}/{activity.estimatedHours}h
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/odos/activities/${activity._id}`);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No activities found matching your filters.
          </Alert>
        )}
      </TabPanel>

      {/* Activity Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedActivity && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedActivity.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip label={selectedActivity.status} size="small" color={getStatusColor(selectedActivity.status)} />
                  <Chip
                    label={selectedActivity.priority}
                    size="small"
                    sx={{ bgcolor: getPriorityColor(selectedActivity.priority), color: 'white' }}
                  />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Activity ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedActivity.activityId}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {selectedActivity.description || 'No description provided'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Module
                  </Typography>
                  <Typography variant="body2">
                    {getModuleName(selectedActivity.module)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2">{selectedActivity.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Estimated Hours
                  </Typography>
                  <Typography variant="body2">
                    {selectedActivity.estimatedHours || 'Not estimated'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Actual Hours
                  </Typography>
                  <Typography variant="body2">
                    {selectedActivity.actualHours || 0}
                  </Typography>
                </Grid>
                {selectedActivity.dueDate && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedActivity.dueDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {selectedActivity.acceptanceCriteria && selectedActivity.acceptanceCriteria.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Acceptance Criteria
                    </Typography>
                    <List dense>
                      {selectedActivity.acceptanceCriteria.map((criteria, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            {criteria.completed ? (
                              <CheckCircle color="success" />
                            ) : (
                              <RadioButtonUnchecked color="action" />
                            )}
                          </ListItemAvatar>
                          <ListItemText primary={criteria.description} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleCloseDialog();
                  navigate(`/odos/activities/${selectedActivity._id}`);
                }}
                sx={{ bgcolor: 'primary.main' }}
              >
                Edit Activity
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ODOSActivities;
